import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Leaf, Heart, Sparkles, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// 診断の質問データ
const questions = [
  "つい予定を詰め込みすぎてしまう。",
  "休んでいると『怠けている』と感じて落ち着かない。",
  "頼まれると断れない。",
  "成果や他人の評価が気になる。",
  "誰かに本音を話せていない気がする。",
  "SNSや他人の幸せを見ると焦りを感じる。",
  "一人の時間が多く、寂しさを感じることがある。",
  "人の優しさを受け取るのが苦手だ。",
  "『自分が本当にやりたいこと』が分からない。",
  "何か満たされない感覚がある。",
  "新しいことを始めたいが、方向が定まらない。",
  "今の自分にどこかしっくりきていない。",
  "ちょっとしたことで気分が大きく変わる。",
  "不安や怒りなど感情の波にのまれやすい。",
  "相手の言葉に敏感に反応してしまう。",
  "気分が落ちたとき、なかなか立ち直れない。",
  "何をしても心が動かないと感じることがある。",
  "以前好きだったことに興味が持てない。",
  "体も心も重く、やる気が出ない。",
  "生きる意味や目的を見失っている気がする。"
];

// 診断タイプの定義
const diagnosisTypes = {
  burnout: {
    name: "燃え尽きタイプ",
    description: "頑張りすぎて疲れ切っているあなた。まずは休息を大切にしましょう。",
    advice: "完璧を求めすぎず、自分に優しくすることから始めてみてください。小さな休息を意識的に取り入れることが大切です。",
    color: "from-orange-400 to-red-400"
  },
  lonely: {
    name: "孤独感タイプ",
    description: "つながりを求めているあなた。人との温かい関係を築いていきましょう。",
    advice: "小さな一歩から始めて、信頼できる人との関係を深めることを意識してみてください。",
    color: "from-blue-400 to-purple-400"
  },
  lost: {
    name: "迷いタイプ",
    description: "自分らしさを探しているあなた。内なる声に耳を傾けてみましょう。",
    advice: "焦らずに、今の自分を受け入れながら、小さな興味や関心を大切にしてください。",
    color: "from-green-400 to-teal-400"
  },
  unstable: {
    name: "感情不安定タイプ",
    description: "心の波が大きいあなた。感情との上手な付き合い方を学びましょう。",
    advice: "深呼吸や瞑想など、心を落ち着ける習慣を取り入れることをおすすめします。",
    color: "from-purple-400 to-pink-400"
  },
  depressed: {
    name: "うつ傾向タイプ",
    description: "心が重いあなた。専門家のサポートを受けることも大切です。",
    advice: "一人で抱え込まず、信頼できる人や専門家に相談することを検討してください。",
    color: "from-gray-400 to-slate-400"
  }
};

const DetailedDiagnosis = () => {
  const [currentStep, setCurrentStep] = useState<'intro' | 'questions' | 'result'>('intro');
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState<number[]>(new Array(20).fill(0));
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const { toast } = useToast();

  const handleAnswerChange = (questionIndex: number, value: string) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = parseInt(value);
    setAnswers(newAnswers);
  };

  const calculateResult = () => {
    // 各カテゴリーのスコア計算
    const burnoutScore = answers.slice(0, 4).reduce((sum, score) => sum + score, 0);
    const lonelyScore = answers.slice(4, 8).reduce((sum, score) => sum + score, 0);
    const lostScore = answers.slice(8, 12).reduce((sum, score) => sum + score, 0);
    const unstableScore = answers.slice(12, 16).reduce((sum, score) => sum + score, 0);
    const depressedScore = answers.slice(16, 20).reduce((sum, score) => sum + score, 0);

    const scores = {
      burnout: burnoutScore,
      lonely: lonelyScore,
      lost: lostScore,
      unstable: unstableScore,
      depressed: depressedScore
    };

    // 最も高いスコアのタイプを判定
    const maxScore = Math.max(...Object.values(scores));
    const dominantType = Object.keys(scores).find(key => scores[key as keyof typeof scores] === maxScore) as keyof typeof diagnosisTypes;

    return {
      type: dominantType,
      scores,
      totalScore: Object.values(scores).reduce((sum, score) => sum + score, 0)
    };
  };

  const saveResultToDatabase = async (diagnosisResult?: any) => {
    const resultToSend = diagnosisResult || result;
    if (!resultToSend || !email) {
      console.log('データベース保存スキップ: 結果またはメールアドレスが不足', { resultToSend: !!resultToSend, email });
      return;
    }
    
    console.log('=== 診断結果をデータベースに保存中 ===');
    
    setIsEmailSending(true);
    
    try {
      const typeInfo = diagnosisTypes[resultToSend.type];
      
      const { data, error } = await supabase
        .from('diagnosis_results_2025_11_12_13_00')
        .insert({
          email: email,
          diagnosis_type: 'detailed',
          result_type: resultToSend.type,
          scores: resultToSend.scores,
          total_score: resultToSend.totalScore,
          type_info: typeInfo
        });
      
      if (error) {
        console.error('データベース保存エラー:', error);
        throw error;
      }
      
      console.log('診断結果をデータベースに保存しました:', data);
      
      toast({
        title: "診断結果が保存されました",
        description: "診断が完了し、結果が保存されました。",
      });
      
    } catch (error) {
      console.error('データベース保存エラー:', error);
      
      toast({
        title: "診断結果が表示されました",
        description: "診断が完了しました。結果をご確認ください。",
      });
    } finally {
      setIsEmailSending(false);
      console.log('=== 診断処理終了 ===');
    }
  };

  const handleSubmit = async () => {
    if (answers.every(answer => answer > 0)) {
      const diagnosisResult = calculateResult();
      setResult(diagnosisResult);
      setCurrentStep('result');
      
      // 自動的にデータベースに保存
      await saveResultToDatabase(diagnosisResult);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (currentStep === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-zen">
          <CardHeader className="text-center space-y-6">
            <div className="flex justify-center items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  戻る
                </Button>
              </Link>
              <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-full">
                <Leaf className="w-12 h-12 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-zen bg-clip-text text-transparent">
              北欧×禅 しあわせ体質診断
            </CardTitle>
            <p className="text-lg text-muted-foreground leading-relaxed">
              あなたの"しあわせ体質"を見つける20の質問です。<br />
              直感でお答えください。
            </p>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                各項目は以下の5段階で評価してください：<br />
                1. まったく当てはまらない<br />
                2. あまり当てはまらない<br />
                3. どちらともいえない<br />
                4. わりと当てはまる<br />
                5. とても当てはまる
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-base font-medium">メールアドレス *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2"
                required
              />
            </div>
            <Button 
              onClick={() => setCurrentStep('questions')} 
              disabled={!email}
              className="w-full bg-gradient-zen hover:opacity-90 transition-zen"
              size="lg"
            >
              診断を開始する
              <Sparkles className="ml-2 w-5 h-5" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 'questions') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-zen">
          <CardHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">質問 {currentQuestion + 1} / {questions.length}</CardTitle>
                <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="text-center">
              <h3 className="text-lg font-medium leading-relaxed mb-6">
                {questions[currentQuestion]}
              </h3>
              <RadioGroup
                value={answers[currentQuestion] > 0 ? answers[currentQuestion].toString() : ""}
                onValueChange={(value) => handleAnswerChange(currentQuestion, value)}
                className="grid grid-cols-5 gap-4"
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <div key={value} className="flex flex-col items-center space-y-2">
                    <RadioGroupItem value={value.toString()} id={`q${currentQuestion}-${value}`} />
                    <Label htmlFor={`q${currentQuestion}-${value}`} className="text-sm text-center">
                      {value}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <div className="grid grid-cols-5 gap-4 mt-2 text-xs text-muted-foreground">
                <span>全く当て<br />はまらない</span>
                <span>あまり当て<br />はまらない</span>
                <span>どちらとも<br />いえない</span>
                <span>わりと当て<br />はまる</span>
                <span>とても当て<br />はまる</span>
              </div>
            </div>
            
            <div className="flex justify-between">
              <Button 
                onClick={prevQuestion} 
                disabled={currentQuestion === 0}
                variant="outline"
              >
                前の質問
              </Button>
              
              {currentQuestion === questions.length - 1 ? (
                <Button 
                  onClick={handleSubmit}
                  disabled={answers[currentQuestion] === 0}
                  className="bg-gradient-zen hover:opacity-90 transition-zen"
                >
                  診断結果を見る
                  <Heart className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button 
                  onClick={nextQuestion}
                  disabled={answers[currentQuestion] === 0}
                  className="bg-gradient-zen hover:opacity-90 transition-zen"
                >
                  次の質問
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 'result' && result) {
    const typeInfo = diagnosisTypes[result.type];
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-zen">
          <CardHeader className="text-center space-y-6">
            <div className="flex justify-center">
              <div className={`p-6 bg-gradient-to-br ${typeInfo.color} rounded-full`}>
                <Heart className="w-12 h-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              あなたは「{typeInfo.name}」です
            </CardTitle>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {typeInfo.description}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 p-6 rounded-lg">
              <h4 className="font-semibold mb-3">アドバイス</h4>
              <p className="text-muted-foreground leading-relaxed">
                {typeInfo.advice}
              </p>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-semibold">詳細スコア</h4>
              {Object.entries(result.scores).map(([key, score]) => {
                const type = diagnosisTypes[key as keyof typeof diagnosisTypes];
                const percentage = (score / 20) * 100;
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{type.name}</span>
                      <span>{score}/20</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-center">
              <Button 
                onClick={() => {
                  setCurrentStep('intro');
                  setAnswers(new Array(20).fill(0));
                  setCurrentQuestion(0);
                  setResult(null);
                }}
                variant="outline"
                className="px-8"
              >
                もう一度診断する
              </Button>

            </div>

            <div className="text-center space-y-4">
              <Link to="/">
                <Button variant="ghost">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  メニューに戻る
                </Button>
              </Link>
              
              {/* 外部サイトへの戻るボタン */}
              <div className="pt-4 border-t border-muted">
                <a 
                  href="https://hokuou-wellbeing.jp/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-gradient-zen hover:opacity-90 transition-zen text-white border-none"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    北欧ウェルビーイングに戻る
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default DetailedDiagnosis;